import type { FutureBoard, SeriesEvent, StatusFilter, Venue, VenueBook } from './types';
import { TERMINAL_STATUSES } from '@/lib/sport/marketLifecycle';
import { observedVolume } from '@/lib/market/tickerScalars';

export type FuturesBoard = SeriesEvent | FutureBoard;
export const isPolyFutureBoard = (board: FuturesBoard): board is FutureBoard => 'legs' in board;

/** Display grouping is never execution identity. */
export interface FuturesSelection {
  sport: FuturesBoard['sport'];
  boardKey: string;
  venue: Venue;
  ticker: string;
}

export type StandaloneSelection = FuturesSelection & { kind?: 'future' | 'golf-match' };

export function futuresStatusMatches(status: string | undefined, filter: StatusFilter): boolean {
  const terminal = TERMINAL_STATUSES.has(status ?? '');
  return filter === 'all' || (filter === 'settled' ? terminal : !terminal);
}

const filteredBoards = new WeakMap<FutureBoard[], Map<StatusFilter, FutureBoard[]>>();

/** Unrelated game ticks must not copy/sort the entire futures population. */
export function filterFutureBoards(boards: FutureBoard[], filter: StatusFilter): FutureBoard[] {
  let byFilter = filteredBoards.get(boards);
  if (!byFilter) { byFilter = new Map(); filteredBoards.set(boards, byFilter); }
  const cached = byFilter.get(filter);
  if (cached) return cached;
  const result = boards.flatMap((board) => {
    const legs = board.legs.filter((leg) => futuresStatusMatches(leg.status, filter));
    return legs.length === 0 ? [] : [legs.length === board.legs.length ? board : { ...board, legs }];
  });
  byFilter.set(filter, result);
  return result;
}

export function futuresBookTradable(book: VenueBook | null, venue: Venue): boolean {
  if (!book || !['open', 'active'].includes(book.status ?? '') || book.freshness === 'stale') return false;
  if (book.priceCents == null || !Number.isFinite(book.priceCents) || book.priceCents <= 0 || book.priceCents >= 100) return false;
  if (venue === 'kalshi') return !!book.marketTicker;
  return !!book.marketTicker && !!book.conditionId && !!book.tokenId && !!book.noTokenId
    && book.tokenId !== book.noTokenId && typeof book.negRisk === 'boolean'
    && ['0.1', '0.01', '0.001', '0.0001'].includes(book.tickSize ?? '');
}

/** Quote changes do not reset an in-progress limit draft; identity changes do. */
export function futuresBookIdentity(book: VenueBook, venue: Venue): string {
  return JSON.stringify([venue, book.marketTicker, book.exchangeIndex, book.exchangeIndexState,
    book.conditionId, book.tokenId, book.noTokenId, book.tickSize, book.negRisk]);
}

export function futuresRows(board: FuturesBoard) {
  return isPolyFutureBoard(board)
    ? board.legs.map((leg) => ({ ticker: leg.ticker, name: leg.name, detail: leg.kindLabel,
      price: leg.priceCents, volume: leg.volume, status: leg.status }))
    : board.outcomes.map((outcome) => ({ ticker: outcome.ticker, name: outcome.name,
      detail: outcome.code, price: outcome.kalshi?.priceCents ?? null,
      // null (dash, sorted as unknown) while the ticker authority is unconfirmed (DNL-R6-P1).
      volume: observedVolume(outcome.kalshi), status: outcome.kalshi?.status }));
}
